import { ExportFormat, ResizeMode } from '@/core/export/types'
import { assertJpegQuality } from '@/core/export/validate'
import type { Point, Rect, Size } from '@/core/geometry'
import { getEncoder } from '@/core/render/encoders'
import { createOffscreenRenderSurface } from '@/core/render/renderDocument'

export const QUALITY_WINDOW_SIZE = 512
export const QUALITY_DIFF_GAIN = 8

export function windowRectAround(point: Point, outputSize: Size): Rect {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    throw new Error('Quality loupe point must contain finite coordinates')
  }
  if (!Number.isInteger(outputSize.width) || !Number.isInteger(outputSize.height)
    || outputSize.width <= 0 || outputSize.height <= 0) {
    throw new Error('Quality loupe output size must contain positive integer dimensions')
  }
  const width = Math.min(QUALITY_WINDOW_SIZE, outputSize.width)
  const height = Math.min(QUALITY_WINDOW_SIZE, outputSize.height)
  return {
    x: Math.min(outputSize.width - width, Math.max(0, Math.round(point.x - width / 2))),
    y: Math.min(outputSize.height - height, Math.max(0, Math.round(point.y - height / 2))),
    width,
    height,
  }
}

export async function encodeWindow(
  canvas: OffscreenCanvas,
  rect: Rect,
  quality: number,
): Promise<{ jpeg: ImageBitmap; bytes: number }> {
  assertJpegQuality(quality)
  if (![rect.x, rect.y, rect.width, rect.height].every(Number.isInteger)
    || rect.x < 0 || rect.y < 0 || rect.width <= 0 || rect.height <= 0
    || rect.x + rect.width > canvas.width || rect.y + rect.height > canvas.height) {
    throw new Error('Quality window rectangle must use positive integer pixels inside the rendered output')
  }
  const surface = createOffscreenRenderSurface({ width: rect.width, height: rect.height })
  surface.context.imageSmoothingEnabled = false
  surface.context.drawImage(
    canvas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    rect.width,
    rect.height,
  )
  const blob = await getEncoder(ExportFormat.Jpeg).encode(surface.canvas, {
    format: ExportFormat.Jpeg,
    jpegQuality: quality,
    resizeMode: ResizeMode.Original,
    customLongestSide: null,
  })
  return { jpeg: await createImageBitmap(blob), bytes: blob.size }
}

export function diffAmplified(first: ImageData, second: ImageData, gain: number): ImageData {
  if (first.width !== second.width || first.height !== second.height
    || first.data.length !== second.data.length) {
    throw new Error('Quality difference images must have matching dimensions')
  }
  if (!Number.isFinite(gain) || gain <= 0) {
    throw new Error('Quality difference gain must be a finite positive number')
  }
  const pixels = new Uint8ClampedArray(first.data.length)
  for (let index = 0; index < pixels.length; index += 4) {
    pixels[index] = Math.abs((first.data[index] ?? 0) - (second.data[index] ?? 0)) * gain
    pixels[index + 1] = Math.abs((first.data[index + 1] ?? 0) - (second.data[index + 1] ?? 0)) * gain
    pixels[index + 2] = Math.abs((first.data[index + 2] ?? 0) - (second.data[index + 2] ?? 0)) * gain
    pixels[index + 3] = 255
  }
  return new ImageData(pixels, first.width, first.height)
}
